import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { IEntity } from 'typings/application/entity';
import { ActionKeys } from 'components/Forms/TemplateForms/Onboarding/Components/SubmitButtons';
import { onboardingAPI } from 'api/onboarding/onboardingAPI';
import { formatDateToUTC } from 'helpers/dateHelpers';
import { RangeOptionModel } from 'typings/onboarding/onboarding';
import { onboardingAPIAdapter } from 'apiAdapters/onboarding/onboardingAPIAdapter';
import { OnboardingStatusModel } from 'typings/onboarding/onboarding';
import { useVerificationContext } from 'modules/Onboarding/Contexts/VerificationContext';
import {
  findSocialMediaByType,
  getKeyOfRangeByRangeOption,
  getEntityNameByNameType,
} from 'helpers/crmHelpers';

// constants
import { EntityTypes, SocialMediaTypes } from 'enums/onboarding/crm';
import {
  NumberOfEmployeesKeys,
  NumberOfEmployeesOptions,
} from '../../../../../../../../FormComponents/SelectInputs/CRM/NumberOfEmployeesSelect';
import {
  NumberOfOfficesKeys,
  NumberOfOfficesOptions,
} from '../../../../../../../../FormComponents/SelectInputs/CRM/NumberOfOfficesSelect';

// components
import { Message } from '@ui';
import GeneralInformationForm, {
  FormValuesModel,
} from '../../../../../../GeneralInformationForm';

interface IProps {
  isViewOnly?: boolean;
  applicationId: string;
  onboardingStatus: OnboardingStatusModel;
  onAction?: (isCompleted: boolean, actionType: ActionKeys | null) => void;
}

const GeneralInformation = ({
  onboardingStatus,
  applicationId,
  isViewOnly,
  onAction,
}: IProps) => {
  const { t } = useTranslation('common');
  const verificationContext = useVerificationContext();

  const initialFormValues = useMemo<FormValuesModel>(() => {
    if (onboardingStatus.item) {
      const organizationData = onboardingStatus.item as IEntity;
      const legalName = getEntityNameByNameType(
        organizationData.names,
        EntityTypes.Legal,
      );
      const tradeName = getEntityNameByNameType(
        organizationData.names,
        EntityTypes.Trader,
      );

      return {
        submitActionType: null,
        isSave: false,
        legalName,
        tradeName,
        dateOfIncorporation: organizationData.dateOfIncorporation
          ? formatDateToUTC(organizationData.dateOfIncorporation)
          : null,
        jurisdiction: organizationData.jurisdiction,
        industryCodes: organizationData.industryCodes,
        natureOfBusiness: organizationData.natureOfBusiness,
        numberOfEmployees: organizationData.numberOfEmployees
          ? (getKeyOfRangeByRangeOption(
              organizationData.numberOfEmployees as RangeOptionModel,
              NumberOfEmployeesOptions,
            ) as NumberOfEmployeesKeys)
          : null,
        numberOfOffices: organizationData.numberOfOffices
          ? (getKeyOfRangeByRangeOption(
              organizationData.numberOfOffices as RangeOptionModel,
              NumberOfOfficesOptions,
            ) as NumberOfOfficesKeys)
          : null,
        registrationNumber: organizationData.registrationNumber,
        socialMedia: {
          websiteLink: organizationData.socialMedia
            ? findSocialMediaByType(
                SocialMediaTypes.WebSite,
                organizationData.socialMedia,
              )
            : '',
          twitterLink: organizationData.socialMedia
            ? findSocialMediaByType(
                SocialMediaTypes.Twitter,
                organizationData.socialMedia,
              )
            : '',
          facebookLink: organizationData.socialMedia
            ? findSocialMediaByType(
                SocialMediaTypes.Facebook,
                organizationData.socialMedia,
              )
            : '',
          linkedInLink: organizationData.socialMedia
            ? findSocialMediaByType(
                SocialMediaTypes.LinkedIn,
                organizationData.socialMedia,
              )
            : '',
        },
      };
    } else {
      return {
        submitActionType: null,
        isSave: false,
        legalName: '',
        tradeName: '',
        dateOfIncorporation: null,
        jurisdiction: '',
        industryCodes: [],
        natureOfBusiness: '',
        numberOfEmployees: null,
        numberOfOffices: null,
        registrationNumber: '',
        socialMedia: {
          websiteLink: '',
          twitterLink: '',
          facebookLink: '',
          linkedInLink: '',
        },
      };
    }
  }, [onboardingStatus]);

  const handleOnSubmit = async (values: FormValuesModel) => {
    await verificationContext?.handleVerificationTabSubmitWithLoader(
      async () => {
        const formattedBody =
          onboardingAPIAdapter.submitApplicantInformationOrganizationStep(
            values,
          );
        await onboardingAPI.submitApplicantInformationOrganizationStep(
          formattedBody,
          applicationId,
          onboardingStatus.item._id,
        );
        Message.success(t('success_save'));
        onAction && onAction(true, values.submitActionType);
      },
    );
  };

  return (
    <GeneralInformationForm
      formRef={verificationContext?.refState}
      disabled={isViewOnly}
      initialFormValues={initialFormValues}
      onSubmit={handleOnSubmit}
      showOnlySaveButton
    />
  );
};

export default GeneralInformation;
